import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { bakeMarkupIntoItems } from "@/lib/markup";
import { Trash2, FileText } from "lucide-react";
import { toast } from "sonner";

export default function Jobs() {
  const [jobs, setJobs] = useState([]);

  const load = () => api.get("/jobs").then((r) => setJobs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this quote?")) return;
    await api.delete(`/jobs/${id}`);
    load();
  };

  const toInvoice = async (j) => {
    try {
      const items = [
        ...(j.labour_hours > 0
          ? [{ description: "Labour", quantity: j.labour_hours, unit_price: j.hourly_rate, kind: "labour" }]
          : []),
        ...(j.items || []),
      ];
      // Bake markup INVISIBLY into each line so the client invoice doesn't
      // expose the tradie's markup %. Total $$ stays identical.
      const itemsWithMarkup = bakeMarkupIntoItems(items, j.markup_percent);
      const { data } = await api.post("/invoices", {
        job_id: j.id,
        client_id: j.client_id || null,
        client_name: j.client_name || null,
        items: itemsWithMarkup,
        gst_inclusive: j.gst_inclusive || false,
      });
      toast.success(`Invoice ${data.invoice_number} created`);
      window.location.href = `/app/invoices/${data.id}`;
    } catch {
      toast.error("Could not create invoice");
    }
  };

  return (
    <div className="stagger-in">
      <div className="label-mono">/ SAVED QUOTES</div>
      <h1 className="font-display text-5xl tracking-tight mt-2">Quotes & Jobs</h1>
      <p className="text-zinc-400 mt-2">Everything you've priced up.</p>

      <div className="mt-10 border border-[var(--border)] overflow-x-auto" data-testid="jobs-list">
        <div className="min-w-[720px]">
        <div className="grid grid-cols-12 gap-0 text-xs font-mono uppercase tracking-wider text-zinc-500 border-b border-[var(--border)]">
          <div className="col-span-4 p-3">Title</div>
          <div className="col-span-3 p-3 border-l border-[var(--border)]">Client</div>
          <div className="col-span-2 p-3 border-l border-[var(--border)]">Subtotal</div>
          <div className="col-span-2 p-3 border-l border-[var(--border)]">Total</div>
          <div className="col-span-1 p-3 border-l border-[var(--border)]" />
        </div>
        {jobs.length === 0 && (
          <div className="p-10 text-center text-zinc-500 font-mono text-sm">
            No quotes yet.{" "}
            <Link to="/app/calc/quote" className="text-[var(--primary)]">
              Build one
            </Link>
          </div>
        )}
        {jobs.map((j) => (
          <div
            key={j.id}
            className="grid grid-cols-12 gap-0 text-sm font-mono border-b border-[var(--border)] last:border-b-0"
            data-testid={`job-row-${j.id}`}
          >
            <div className="col-span-4 p-3 truncate">{j.title}</div>
            <div className="col-span-3 p-3 border-l border-[var(--border)] truncate text-zinc-400">
              {j.client_name || "—"}
            </div>
            <div className="col-span-2 p-3 border-l border-[var(--border)]">{fmtMoney(j.subtotal)}</div>
            <div className="col-span-2 p-3 border-l border-[var(--border)] text-[var(--primary)]">
              {fmtMoney(j.total)}
            </div>
            <div className="col-span-1 border-l border-[var(--border)] flex">
              <button
                onClick={() => toInvoice(j)}
                className="flex-1 border-r border-[var(--border)] text-zinc-500 hover:text-[var(--primary)] flex items-center justify-center"
                data-testid={`job-invoice-${j.id}`}
                title="Create invoice"
              >
                <FileText size={13} />
              </button>
              <button
                onClick={() => remove(j.id)}
                className="flex-1 text-zinc-500 hover:text-[var(--error)] flex items-center justify-center"
                data-testid={`job-delete-${j.id}`}
              >
                <Trash2 size={13} />
              </button>
            </div>
          </div>
        ))}
        </div>
      </div>
    </div>
  );
}
